import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { GlobalContext } from "../../Context/GlobalContext";
import { Loader2, Users, UserCog, X } from "lucide-react";
import { toast } from "react-hot-toast";

import useSuperAdmin from "../Services/SuperAdminServices/useSuperAdmin";
import useClients from "../Services/ClientServices/useClient";

const AdminClientAssignment = () => {
  const {getAllAdminUsers, getAssignedClients, assignAdminUserToClient, deleteAssignedClient}=useSuperAdmin();
  const {fetchAll}=useClients();


  const { api, adminId, AllClients } =
    useContext(GlobalContext);

  const [admins, setAdmins] = useState([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [adminSearch, setAdminSearch] = useState("");

  const [selectedAdmin, setSelectedAdmin] = useState(null);

  const [clientSearch, setClientSearch] = useState("");
  const [selectedClientCodes, setSelectedClientCodes] = useState([]);

  const [clientsLoading, setClientsLoading] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);

  const [assignedClients, setAssignedClients] = useState([]); // [{id, us_code}]
  const [assignedLoading, setAssignedLoading] = useState(false);
  const [unassignLoadingId, setUnassignLoadingId] = useState(null);

  // ========= LOAD ADMINS =========
const fetchAdmins = useCallback(
  async () => {
    if (!adminId) return;

    setAdminsLoading(true);

    try {
      const result =
        await getAllAdminUsers(adminId);

      if (result.success) {
        setAdmins(result.data);
      } else {
        toast.error(result.message);
      }
    } catch (e) {
      toast.error(
        "Failed to fetch admin users."
      );
    } finally {
      setAdminsLoading(false);
    }
  },
  [adminId]
);


  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  // ========= LOAD CLIENTS (IF EMPTY) =========
  useEffect(() => {
    const ensureClients = async () => {
      if (!AllClients || AllClients.length === 0) {
        setClientsLoading(true);
        try {
          await fetchAll();
        } catch (e) {
          // toast handled inside context if needed
        } finally {
          setClientsLoading(false);
        }
      }
    };
    ensureClients();
  }, [AllClients, fetchAll]);

  // ========= LOAD ASSIGNED CLIENTS FOR SELECTED ADMIN =========
const fetchAssignedForAdmin = useCallback(
  async (admId) => {
    if (!admId) return;

    setAssignedLoading(true);

    try {
      const result =
        await getAssignedClients(admId);

      if (result.success) {
        const list = result.data;

        setAssignedClients(list);

        // Pre-select client codes
        const codes = list
          .map((m) => m.us_code)
          .filter(Boolean);

        setSelectedClientCodes(codes);
      } else {
        setAssignedClients([]);
        setSelectedClientCodes([]);
        toast.error(result.message);
      }
    } catch (e) {
      setAssignedClients([]);
      setSelectedClientCodes([]);
      toast.error(
        "Failed to fetch assigned clients."
      );
    } finally {
      setAssignedLoading(false);
    }
  },
  [getAssignedClients]
);


  // ========= ADMIN LIST FILTERING =========
  const filteredAdmins = useMemo(() => {
    const q = adminSearch.trim().toLowerCase();
    if (!q) return admins;

    return admins.filter((adm) => {
      const name = (adm.admin_username || "").toLowerCase();
      const first = (adm.first_name || "").toLowerCase();
      const last = (adm.last_name || "").toLowerCase();
      const emp = (adm.emp_ID || "").toLowerCase();
      const login = (adm.admin_loginid || "").toLowerCase();

      return (
        name.includes(q) ||
        first.includes(q) ||
        last.includes(q) ||
        emp.includes(q) ||
        login.includes(q)
      );
    });
  }, [admins, adminSearch]);

  // ========= CLIENT FILTERING =========
  const filteredClients = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return AllClients || [];

    return (AllClients || []).filter((c) => {
      const name = (c.us_name || "").toLowerCase();
      const code = (c.us_code || "").toLowerCase();
      const mobile = (c.us_loginid || "").toLowerCase();
      const org = (c.organization_name || "").toLowerCase();
      return (
        name.includes(q) ||
        code.includes(q) ||
        mobile.includes(q) ||
        org.includes(q)
      );
    });
  }, [AllClients, clientSearch]);

  // ========= CLIENT SELECTION =========
  const toggleClientSelection = (clientCode) => {
    setSelectedClientCodes((prev) =>
      prev.includes(clientCode)
        ? prev.filter((c) => c !== clientCode)
        : [...prev, clientCode]
    );
  };

  const areAllFilteredSelected =
    filteredClients.length > 0 &&
    filteredClients.every((c) => selectedClientCodes.includes(c.us_code));

  const toggleSelectAllFiltered = () => {
    if (areAllFilteredSelected) {
      const filteredCodes = filteredClients.map((c) => c.us_code);
      setSelectedClientCodes((prev) =>
        prev.filter((code) => !filteredCodes.includes(code))
      );
    } else {
      const filteredCodes = filteredClients.map((c) => c.us_code);
      setSelectedClientCodes((prev) => {
        const set = new Set(prev);
        filteredCodes.forEach((c) => set.add(c));
        return Array.from(set);
      });
    }
  };

  // ========= ADMIN SELECTION =========
  const handleSelectAdmin = (adm) => {
    setSelectedAdmin(adm);
    setAssignedClients([]);
    setSelectedClientCodes([]);
    fetchAssignedForAdmin(adm.id);
  };

  // ========= DECORATED ASSIGNED CLIENTS (JOIN WITH FULL CLIENT DATA) =========
  const decoratedAssignedClients = useMemo(() => {
    if (!assignedClients || assignedClients.length === 0) return [];
    const all = AllClients || [];
    return assignedClients.map((m) => {
      const client = all.find((c) => c.us_code === m.us_code);
      return { ...m, client };
    });
  }, [assignedClients, AllClients]);

  const assignedCodesSet = useMemo(
    () => new Set(assignedClients.map((m) => m.us_code)),
    [assignedClients]
  );

  // ========= ASSIGN API CALL =========
const handleAssign = async () => {
  if (!selectedAdmin) {
    toast.error(
      "Please select an admin user first."
    );
    return;
  }

  if (!selectedClientCodes.length) {
    toast.error(
      "Please select at least one client to assign."
    );
    return;
  }

  // Only send codes that are not already assigned
  const newToAssign =
    selectedClientCodes.filter(
      (code) =>
        !assignedCodesSet.has(code)
    );

  if (!newToAssign.length) {
    toast(
      "No new clients to assign. All selected are already mapped.",
      { icon: "ℹ️" }
    );
    return;
  }

  setAssignLoading(true);

  try {
    const result =
      await assignAdminUserToClient({
        adminId: selectedAdmin.id,
        users: newToAssign,
      });

    if (result.success) {
      toast.success(result.message);

      await fetchAssignedForAdmin(
        selectedAdmin.id
      );
    } else {
      toast.error(result.message);
    }
  } catch (e) {
    toast.error(
      "Failed to assign clients."
    );
  } finally {
    setAssignLoading(false);
  }
};

  // ========= UNASSIGN API CALL =========
const handleUnassign = async (mapping) => {
  if (!mapping?.id) return;

  setUnassignLoadingId(mapping.id);

  try {
    const result =
      await deleteAssignedClient(
        mapping.id
      );

    if (result.success) {
      toast.success(result.message);

      // Remove from assigned list
      setAssignedClients((prev) =>
        prev.filter(
          (m) => m.id !== mapping.id
        )
      );

      // Remove from selected codes
      setSelectedClientCodes((prev) =>
        prev.filter(
          (code) =>
            code !== mapping.us_code
        )
      );
    } else {
      toast.error(result.message);
    }
  } catch (e) {
    toast.error(
      "Failed to unassign client."
    );
  } finally {
    setUnassignLoadingId(null);
  }
};

  return (
    <div className="space-y-4">
      {/* Content: keep both columns same height */}
      <div className="grid items-stretch gap-4 lg:grid-cols-2">
        {/* LEFT: Admin list */}
        <div className="h-full rounded-xl border border-gray-200 bg-white shadow-sm flex flex-col">
          <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                <UserCog className="h-4 w-4" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-gray-900">
                  Admin Users
                </h2>
                <p className="text-[11px] text-gray-500">
                  Search and select an admin to manage their mapped clients.
                </p>
              </div>
            </div>

            <div className="relative">
              <input
                type="text"
                value={adminSearch}
                onChange={(e) => setAdminSearch(e.target.value)}
                placeholder="Search admin…"
                className="h-8 w-40 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs text-gray-800 placeholder:text-gray-400 focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>
          </div>

          <div className="max-h-[460px] flex-1 overflow-auto">
            {adminsLoading ? (
              <div className="flex items-center justify-center py-10 text-xs text-gray-500">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading admin users…
              </div>
            ) : filteredAdmins.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-gray-500">
                No admin users found.
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {filteredAdmins.map((adm) => {
                  const isActive = selectedAdmin?.id === adm.id;
                  const assignedCountForThisAdmin =
                    selectedAdmin?.id === adm.id
                      ? assignedClients.length
                      : undefined;

                  return (
                    <li key={adm.id}>
                      <button
                        type="button"
                        onClick={() => handleSelectAdmin(adm)}
                        className={`flex w-full items-start justify-between gap-3 px-4 py-3 text-left text-xs transition ${
                          isActive
                            ? "bg-emerald-50/70"
                            : "hover:bg-gray-50/60"
                        }`}
                      >
                        <div className="flex flex-col gap-0.5">
                          <span className="text-[13px] font-semibold text-gray-900">
                            {adm.admin_username}
                          </span>
                          <span className="text-[11px] text-gray-500">
                            {adm.first_name} {adm.last_name}
                          </span>

                          <div className="mt-1 flex flex-wrap gap-1">
                            {adm.emp_ID && (
                              <span className="rounded-full bg-gray-100 px-2 py-[2px] text-[10px] font-medium text-gray-700">
                                EMP: {adm.emp_ID}
                              </span>
                            )}
                            {adm.admin_loginid && (
                              <span className="rounded-full bg-gray-100 px-2 py-[2px] text-[10px] font-medium text-gray-700">
                                Login: {adm.admin_loginid}
                              </span>
                            )}
                            {isActive &&
                              typeof assignedCountForThisAdmin === "number" && (
                                <span className="rounded-full bg-emerald-100 px-2 py-[2px] text-[10px] font-semibold text-emerald-700">
                                  {assignedCountForThisAdmin} client
                                  {assignedCountForThisAdmin === 1 ? "" : "s"}
                                </span>
                              )}
                          </div>
                        </div>

                        {isActive && (
                          <span className="mt-1 inline-flex items-center rounded-full bg-emerald-100 px-2 py-[2px] text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                            Selected
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
            <button
              type="button"
              onClick={fetchAdmins}
              className="inline-flex items-center gap-1 rounded-md border border-emerald-100 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm transition hover:border-emerald-200 hover:bg-emerald-50"
            >
              {adminsLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Refreshing…
                </>
              ) : (
                <>
                  <Users className="h-4 w-4" />
                  Refresh admins
                </>
              )}
            </button>
          </div>
        </div>

        {/* RIGHT: Client list + assignment */}
        <div className="h-full flex flex-col rounded-xl border border-gray-200 bg-white shadow-sm min-h-[420px]">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
            <div className="flex items-start gap-2">
              <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                <Users className="h-4 w-4" />
              </div>
              <div className="flex flex-col">
                <h2 className="text-sm font-semibold text-gray-900">
                  Clients ({filteredClients.length})
                </h2>
                <p className="text-[11px] text-gray-500">
                  Pick clients to assign to{" "}
                  {selectedAdmin ? (
                    <span className="font-medium text-gray-800">
                      {selectedAdmin.admin_username}
                    </span>
                  ) : (
                    <span className="font-medium text-orange-600">
                      (No admin selected)
                    </span>
                  )}
                  .
                </p>
              </div>
            </div>

            <div className="relative">
              <input
                type="text"
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                placeholder="Search by name, code, mobile, org…"
                className="h-8 w-56 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs text-gray-800 placeholder:text-gray-400 focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>
          </div>

          {selectedAdmin && (
            <div className="border-b border-gray-100 bg-gray-50 px-4 py-2">
              <div className="flex items-center justify-between gap-2">
                <div className="text-[11px] text-gray-600">
                  <span className="font-semibold text-gray-900">
                    {assignedClients.length}
                  </span>{" "}
                  client
                  {assignedClients.length === 1 ? "" : "s"} currently assigned
                  to{" "}
                  <span className="font-medium text-gray-900">
                    {selectedAdmin.admin_username}
                  </span>
                  .
                </div>
                {assignedLoading && (
                  <div className="inline-flex items-center text-[10px] text-gray-500">
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                    loading…
                  </div>
                )}
              </div>

              {decoratedAssignedClients.length > 0 && (
                <div className="mt-2 flex max-h-20 flex-wrap gap-1 overflow-y-auto">
                  {decoratedAssignedClients.map((m) => {
                    const name =
                      m.client?.us_name ||
                      m.client?.organization_name ||
                      m.us_code;
                    const org = m.client?.organization_name;

                    const isRemoving = unassignLoadingId === m.id;

                    return (
                      <div
                        key={m.id}
                        className="group inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-[2px] text-[10px] text-gray-700"
                      >
                        <span className="max-w-[150px] truncate font-medium">
                          {name}
                        </span>
                        <span className="text-[10px] text-gray-500">
                          ({m.us_code})
                          {org ? ` • ${org}` : ""}
                        </span>
                        <button
                          type="button"
                          disabled={isRemoving}
                          onClick={() => handleUnassign(m)}
                          className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full bg-gray-200 text-[10px] text-gray-700 hover:bg-red-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-70"
                          title="Unassign client"
                        >
                          {isRemoving ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <X className="h-3 w-3" />
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="flex items-center border-b border-gray-100 bg-gray-50 px-4 py-2 text-[11px] font-semibold text-gray-600">
            <div className="w-6">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                checked={areAllFilteredSelected && filteredClients.length > 0}
                onChange={toggleSelectAllFiltered}
              />
            </div>
            <div className="w-32">Client Code</div>
            <div className="flex-1">Client Name</div>
            <div className="w-32">Mobile</div>
            <div className="w-40">Organization</div>
          </div>

          <div className="flex-1 max-h-[330px] overflow-auto">
            {clientsLoading ? (
              <div className="flex items-center justify-center py-10 text-xs text-gray-500">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading clients…
              </div>
            ) : filteredClients.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-gray-500">
                No clients found for this filter.
              </div>
            ) : (
              filteredClients.map((client) => {
                const code = client.us_code;
                const checked = selectedClientCodes.includes(code);
                const isAlreadyAssigned = assignedCodesSet.has(code);

                const mappingForRow = assignedClients.find(
                  (m) => m.us_code === code
                );
                const isRowUnassigning =
                  mappingForRow && mappingForRow.id === unassignLoadingId;

                return (
                  <div
                    key={client.id || code}
                    className="flex items-center border-b border-gray-50 px-4 py-2 text-[11px] text-gray-700 hover:bg-gray-50"
                  >
                    <div className="w-6">
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                        checked={checked}
                        onChange={() => toggleClientSelection(code)}
                      />
                    </div>

                    <div className="w-32 flex items-center gap-1">
                      <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-[2px] text-[10px] font-semibold text-gray-800">
                        {client.us_code}
                      </span>

                      {isAlreadyAssigned && (
                        <div className="inline-flex items-center gap-1">
                          <span className="rounded-full bg-emerald-100 px-1.5 py-[1px] text-[9px] font-semibold text-emerald-700">
                            Assigned
                          </span>
                          {mappingForRow && (
                            <button
                              type="button"
                              disabled={isRowUnassigning}
                              onClick={() => handleUnassign(mappingForRow)}
                              className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-gray-200 text-[9px] text-gray-700 hover:bg-red-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-70"
                              title="Remove assignment"
                            >
                              {isRowUnassigning ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <X className="h-3 w-3" />
                              )}
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex-1 truncate">
                      <span className="text-[11px] font-medium text-gray-900">
                        {client.us_name || "-"}
                      </span>
                    </div>

                    <div className="w-32 truncate text-[11px] text-gray-600">
                      {client.us_loginid || "-"}
                    </div>

                    <div className="w-40 truncate text-[11px] text-gray-600">
                      {client.organization_name || "-"}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50 px-4 py-3 text-[11px]">
            <div className="text-gray-600">
              <span className="font-semibold text-gray-900">
                {selectedClientCodes.length}
              </span>{" "}
              client(s) selected
            </div>

            <button
              type="button"
              onClick={handleAssign}
              disabled={
                assignLoading ||
                !selectedAdmin ||
                selectedClientCodes.length === 0
              }
              className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-[11px] font-semibold shadow-sm transition ${
                assignLoading ||
                !selectedAdmin ||
                selectedClientCodes.length === 0
                  ? "cursor-not-allowed bg-gray-200 text-gray-500"
                  : "bg-emerald-600 text-white hover:bg-emerald-700"
              }`}
            >
              {assignLoading ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Assigning…
                </>
              ) : (
                <>
                  <UserCog className="h-3.5 w-3.5" />
                  Assign Selected Clients
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminClientAssignment;
